import React, { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, CheckCircle, Clock, Trash2, Truck, Calendar, Package, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useRoleFlags } from '@/auth/useRoleFlags';
import { SearchableSelect } from "@/components/ui/SearchableSelect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL;

export default function MaterialManagementTab({
  userRole,
  personnelId,
  materialRequests,
  loadingMaterials,
  personnel,
  regions = [],
  parks = [],
  isReadOnly = false, // true = gölge admin, bekleyen taleplerde sadece görüntüleme
  onCreateRequest,
  onDeliverRequest,
  onRejectRequest,
  onDeleteRequest,
  onRefresh
}) {
  // Sprint 2 F4: Dinamik yetki + rol flag'leri
  const _flagsCtx = useRoleFlags(userRole);
  const { has, isAdmin, isMudur, isMudurYardimcisi, isBolgeMuhendisi, isFormen, isParkGorevlisi, isGolgeAdmin } = _flagsCtx;
  // F1-G dört göz: talep sahibi ve talebi sisteme giren kişi talebi hiçbir rolde
  // sonuçlandıramaz (backend `kendi_talebi_karar`). Gizleme yalnız kolaylıktır; karar backend'dedir.
  const isOwnRequest = (request) => {
    const me = String(personnelId || "");
    return !!me && (String(request?.personnel_id || "") === me || String(request?.created_by_id || "") === me);
  };

  const [materialToDelete, setMaterialToDelete] = React.useState(null);
  const [materialToReject, setMaterialToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejecting, setRejecting] = useState(false);
  
  // Filtreleme state'leri
  const [filterMaterialType, setFilterMaterialType] = useState("all");
  const [filterRegion, setFilterRegion] = useState("all");
  // Hiyerarşik personel filtresi (MY > BM > Formen > Park Görevlisi)
  const [filterMY, setFilterMY] = useState("all");
  const [filterBM, setFilterBM] = useState("all");
  const [filterFormen, setFilterFormen] = useState("all");
  const [filterPark, setFilterPark] = useState("all");

  // Tüm hook'lar erken return'lerden önce olmalı (Rendered fewer hooks hatası önlemi)
  const orderedMaterialTypes = useMemo(() => {
    const all = ['Tırmık', 'Yelpaze Tırmık', 'Kürek', 'Çalı Süpürge', 'Faraş', 'Budama Makası', 'Bahçe Hortumu', 'Çöp Poşeti', 'Diğer'];
    const withoutOther = all.filter(type => type !== 'Diğer');
    const sorted = [...withoutOther].sort((a, b) => a.localeCompare(b, 'tr'));
    return [...sorted, 'Diğer'];
  }, []);

  const handleDeleteRequest = async (requestId) => {
    try {
      await onDeleteRequest(requestId);
      setMaterialToDelete(null);
      toast.success("Malzeme talebi silindi");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Silme işlemi başarısız");
    }
  };

  const handleRejectRequest = async () => {
    if (!materialToReject || !rejectionReason.trim()) {
      toast.error("Lütfen red nedenini giriniz");
      return;
    }

    setRejecting(true);
    try {
      await onRejectRequest(materialToReject.id, rejectionReason);
      setMaterialToReject(null);
      setRejectionReason("");
      toast.success("Malzeme talebi reddedildi");
    } catch (error) {
      console.error("Reject error:", error);
      toast.error(error?.response?.data?.error?.message || error?.response?.data?.detail || "Reddetme işlemi başarısız");
    } finally {
      setRejecting(false);
    }
  };

  // Filter requests based on user role
  const getFilteredRequests = () => {
    if (isParkGorevlisi) {
      // Park görevlisi sadece kendi taleplerini görür
      return materialRequests.filter(mr => mr.personnel_id === personnelId);
    } else if (isFormen) {
      // Formen: Kendisi + altındaki park görevlilerinin talepleri
      const myPersonnelIds = personnel
        .filter(p => p.formen_id === personnelId || p.parent_id === personnelId)
        .map(p => p.id);
      myPersonnelIds.push(personnelId); // Kendisini de ekle
      return materialRequests.filter(mr => 
        myPersonnelIds.includes(mr.personnel_id) || mr.formen_id === personnelId
      );
    } else if (isBolgeMuhendisi) {
      // Bölge Mühendisi: Kendisi + altındaki tüm personelin talepleri (formenler + park görevlileri)
      const myFormenIds = personnel
        .filter(p => p.bolge_muhendisi_id === personnelId && p.role === "formen")
        .map(p => p.id);
      const myParkGorevlisiIds = personnel
        .filter(p => p.bolge_muhendisi_id === personnelId || myFormenIds.includes(p.formen_id))
        .map(p => p.id);
      const allMyPersonnelIds = [...new Set([...myFormenIds, ...myParkGorevlisiIds, personnelId])];
      return materialRequests.filter(mr => 
        allMyPersonnelIds.includes(mr.personnel_id) || allMyPersonnelIds.includes(mr.formen_id)
      );
    } else if (isMudurYardimcisi) {
      // Müdür Yardımcısı: Kendi bölgelerindeki personelin talepleri
      // Önce kendine atanmış bölgeleri bul
      const currentUser = personnel.find(p => p.id === personnelId);
      const myRegionIds = currentUser?.region_ids || [];

      if (myRegionIds.length === 0) {
        return materialRequests; // Bölge atanmamışsa hepsini göster
      }

      // Bu bölgelerdeki tüm personeli bul
      const myPersonnelIds = personnel
        .filter(p => myRegionIds.some(rid => p.region_ids?.includes(rid)))
        .map(p => p.id);
      myPersonnelIds.push(personnelId);
      
      return materialRequests.filter(mr => 
        myPersonnelIds.includes(mr.personnel_id) || myPersonnelIds.includes(mr.formen_id)
      );
    }
    return materialRequests; // Admin (müdür) herkesi görür
  };

  const filteredRequests = getFilteredRequests();

  if (loadingMaterials) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p className="mt-2 text-gray-600">Yükleniyor...</p>
      </div>
    );
  }

  // Park görevlisi için boş durum - basit görünüm
  if (filteredRequests.length === 0 && isParkGorevlisi) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Malzeme Taleplerim</h2>
          {has('malzeme.olustur') && (
          <Button
            onClick={onCreateRequest}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Yeni Malzeme Talebi Oluştur
          </Button>
          )}
        </div>
        <Card>
          <CardContent className="text-center py-12">
            <Package className="h-16 w-16 mx-auto text-gray-300" />
            <p className="mt-4 text-gray-500">Henüz hiç malzeme talebi oluşturmadınız.</p>
            <p className="text-sm text-gray-400 mt-2">Yeni bir talep oluşturmak için yukarıdaki butona tıklayın.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Diğer roller için boş durum
  if (filteredRequests.length === 0) {
    return (
      <>
        {has('malzeme.olustur') && (
          <div className="flex justify-end mb-4">
            <Button
              onClick={onCreateRequest}
              className="flex items-center gap-2"
            >
              <Plus className="h-4 w-4" />
              Yeni Malzeme Talebi Oluştur
            </Button>
          </div>
        )}
        <Card>
          <CardContent className="text-center py-12">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mx-auto text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
            </svg>
            <p className="mt-4 text-gray-500">
              {isFormen
                ? "Henüz hiç malzeme talebi bulunmuyor. Kendiniz veya ekibiniz için talep oluşturabilirsiniz."
                : "Henüz hiç malzeme talebi bulunmuyor."}
            </p>
          </CardContent>
        </Card>
      </>
    );
  }

  // Custom SVG icons for materials
  const TirmikIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
      <path d="M12 2v4M8 6V2M16 6V2M6 6h12M7 6l5 16M17 6l-5 16" stroke="#22c55e" strokeWidth="2" strokeLinecap="round"/>
      <path d="M6 6h12v2H6z" fill="#22c55e"/>
    </svg>
  );

  const YelpazeTirmikIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
      <path d="M12 22V10M12 10L4 4M12 10L8 3M12 10L12 2M12 10L16 3M12 10L20 4" stroke="#71717a" strokeWidth="1.5" strokeLinecap="round"/>
      <circle cx="12" cy="10" r="1.5" fill="#71717a"/>
    </svg>
  );

  const KurekIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7">
      {/* Sap - ahşap rengi, çapraz */}
      <path d="M20 1L7 17" stroke="#8B4513" strokeWidth="3" strokeLinecap="round"/>
      {/* Kürek başı - mavi metal, geniş */}
      <path d="M2 14L7 17L10 22L5 23L1 19L2 14Z" fill="#1e40af" stroke="#1e3a8a" strokeWidth="1.5"/>
      {/* Parlaklık */}
      <path d="M3 16L8 20" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );

  const FarasIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-7 h-7">
      {/* Sap - düz yukarı */}
      <path d="M12 1L12 8" stroke="#1e3a8a" strokeWidth="2.5" strokeLinecap="round"/>
      {/* Sap tutma çubuğu */}
      <path d="M9 2L15 2" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round"/>
      {/* Faraş kabı - mavi, düz açıdan */}
      <path d="M4 8L20 8L22 12L20 22L4 22L2 12L4 8Z" fill="#3b82f6" stroke="#1e40af" strokeWidth="1.5"/>
      {/* İç kısım - açık mavi */}
      <path d="M5 10L19 10L20.5 13L19 20L5 20L3.5 13L5 10Z" fill="#93c5fd"/>
    </svg>
  );

  const HortumIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" className="w-6 h-6">
      <path d="M4 12c0-4 3-8 8-8s8 4 8 8" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" fill="none"/>
      <path d="M4 12c0 4 3 8 8 8" stroke="#22c55e" strokeWidth="3" strokeLinecap="round" fill="none"/>
      <circle cx="20" cy="12" r="2" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1"/>
    </svg>
  );

  const materialColors = {
    'Tırmık': { gradient: 'from-green-50 to-green-100', border: 'border-green-300', text: 'text-green-600', textBold: 'text-green-900', bg: 'bg-green-200', icon: 'tirmik' },
    'Yelpaze Tırmık': { gradient: 'from-zinc-50 to-zinc-100', border: 'border-zinc-300', text: 'text-zinc-600', textBold: 'text-zinc-900', bg: 'bg-zinc-200', icon: 'yelpaze' },
    'Kürek': { gradient: 'from-blue-50 to-blue-100', border: 'border-blue-300', text: 'text-blue-600', textBold: 'text-blue-900', bg: 'bg-blue-200', icon: 'kurek' },
    'Çalı Süpürge': { gradient: 'from-amber-50 to-amber-100', border: 'border-amber-300', text: 'text-amber-600', textBold: 'text-amber-900', bg: 'bg-amber-200', icon: '🧹' },
    'Faraş': { gradient: 'from-sky-50 to-sky-100', border: 'border-sky-300', text: 'text-sky-600', textBold: 'text-sky-900', bg: 'bg-sky-200', icon: 'faras' },
    'Budama Makası': { gradient: 'from-rose-50 to-rose-100', border: 'border-rose-300', text: 'text-rose-600', textBold: 'text-rose-900', bg: 'bg-rose-200', icon: '✂️' },
    'Bahçe Hortumu': { gradient: 'from-emerald-50 to-emerald-100', border: 'border-emerald-300', text: 'text-emerald-600', textBold: 'text-emerald-900', bg: 'bg-emerald-200', icon: 'hortum' },
    'Çöp Poşeti': { gradient: 'from-slate-50 to-slate-100', border: 'border-slate-300', text: 'text-slate-600', textBold: 'text-slate-900', bg: 'bg-slate-200', icon: '🗑️' },
    'Diğer': { gradient: 'from-gray-50 to-gray-100', border: 'border-gray-300', text: 'text-gray-600', textBold: 'text-gray-900', bg: 'bg-gray-200', icon: '📦' }
  };

  // Function to render material icon
  const renderMaterialIcon = (iconType) => {
    switch(iconType) {
      case 'tirmik': return <TirmikIcon />;
      case 'yelpaze': return <YelpazeTirmikIcon />;
      case 'kurek': return <KurekIcon />;
      case 'faras': return <FarasIcon />;
      case 'hortum': return <HortumIcon />;
      default: return <span className="text-2xl">{iconType}</span>;
    }
  };

  // Group requests by status
  const groupedRequests = {
    pending: filteredRequests.filter(mr => mr.status === 'pending'),
    delivered: filteredRequests.filter(mr => mr.status === 'delivered'),
    rejected: filteredRequests.filter(mr => mr.status === 'rejected')
  };

  // Park Görevlisi için basit görünüm - Tab yapısı ile
  if (isParkGorevlisi) {
    // Bekleyen talepleri malzeme tipine göre grupla
    const pendingByType = {};
    groupedRequests.pending.forEach(request => {
      const type = request.material_type || 'Diğer';
      if (!pendingByType[type]) pendingByType[type] = [];
      pendingByType[type].push(request);
    });

    // Teslim edilenleri malzeme tipine göre grupla
    const deliveredByType = {};
    groupedRequests.delivered.forEach(request => {
      const type = request.material_type || 'Diğer';
      if (!deliveredByType[type]) deliveredByType[type] = [];
      deliveredByType[type].push(request);
    });

    // Reddedilenleri malzeme tipine göre grupla
    const rejectedByType = {};
    groupedRequests.rejected.forEach(request => {
      const type = request.material_type || 'Diğer';
      if (!rejectedByType[type]) rejectedByType[type] = [];
      rejectedByType[type].push(request);
    });

    // Malzeme tiplerini sırala (veri olanlar önce)
    const pendingTypes = Object.keys(pendingByType).sort((a, b) => a.localeCompare(b, 'tr'));
    const deliveredTypes = Object.keys(deliveredByType).sort((a, b) => a.localeCompare(b, 'tr'));
    const rejectedTypes = Object.keys(rejectedByType).sort((a, b) => a.localeCompare(b, 'tr'));

    return (
      <div className="space-y-4">
        {/* Başlık ve Yeni Talep Butonu */}
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-800">Malzeme Taleplerim</h2>
          {has('malzeme.olustur') && (
          <Button
            onClick={onCreateRequest}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Yeni Malzeme Talebi Oluştur
          </Button>
          )}
        </div>

        {/* Tab Yapısı */}
        <Tabs defaultValue="pending" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="pending" className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Bekleyen ({groupedRequests.pending.length})
            </TabsTrigger>
            <TabsTrigger value="delivered" className="flex items-center gap-2">
              <CheckCircle className="h-4 w-4" />
              Teslim Edilen ({groupedRequests.delivered.length})
            </TabsTrigger>
            <TabsTrigger value="rejected" className="flex items-center gap-2">
              <XCircle className="h-4 w-4" />
              Reddedilen ({groupedRequests.rejected.length})
            </TabsTrigger>
          </TabsList>

          {/* BEKLEYEN TALEPLER TAB */}
          <TabsContent value="pending" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-orange-700">
                  <Clock className="h-5 w-5" />
                  Bekleyen Taleplerim
                </CardTitle>
              </CardHeader>
              <CardContent>
            {groupedRequests.pending.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Bekleyen talebiniz bulunmuyor</p>
            ) : (
              <div className="space-y-4">
                {pendingTypes.map(materialType => {
                  const requests = pendingByType[materialType];
                  const colors = materialColors[materialType] || materialColors['Diğer'];
                  const totalQuantity = requests.reduce((sum, r) => sum + r.quantity, 0);
                  
                  return (
                    <div key={materialType} className={`p-4 rounded-lg border-2 ${colors.border} bg-gradient-to-r ${colors.gradient}`}>
                      {/* Malzeme Tipi Başlığı */}
                      <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-200">
                        <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center`}>
                          {renderMaterialIcon(colors.icon)}
                        </div>
                        <div>
                          <p className={`font-bold text-lg ${colors.textBold}`}>{materialType}</p>
                          <p className="text-sm text-gray-600">{requests.length} talep • Toplam {totalQuantity} adet</p>
                        </div>
                      </div>
                      
                      {/* Bu tipteki talepler */}
                      <div className="space-y-2 pl-2">
                        {requests
                          .sort((a, b) => new Date(b.requested_date || b.created_at) - new Date(a.requested_date || a.created_at))
                          .map(request => (
                          <div key={request.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                            <div>
                              <span className="font-medium">{request.quantity} adet</span>
                              {request.description && (
                                <span className="text-sm text-gray-500 ml-2">- {request.description}</span>
                              )}
                              {request.created_by_name && request.created_by_id !== request.personnel_id && (
                                <span className="text-xs text-blue-600 ml-2">({request.created_by_name} tarafından)</span>
                              )}
                            </div>
                            <div className="text-right">
                              <Badge variant="outline" className="bg-orange-100 text-orange-700 border-orange-300 text-xs">
                                Beklemede
                              </Badge>
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(request.requested_date || request.created_at).toLocaleDateString('tr-TR')}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* TESLİM EDİLEN MALZEMELER TAB */}
          <TabsContent value="delivered" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  Teslim Edilen Malzemelerim
                </CardTitle>
              </CardHeader>
              <CardContent>
                {groupedRequests.delivered.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Henüz teslim edilen malzemeniz bulunmuyor</p>
                ) : (
                  <div className="space-y-4">
                    {deliveredTypes.map(materialType => {
                      const requests = deliveredByType[materialType];
                      const colors = materialColors[materialType] || materialColors['Diğer'];
                      const totalQuantity = requests.reduce((sum, r) => sum + r.quantity, 0);
                      
                      return (
                        <div key={materialType} className="p-4 rounded-lg border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
                          <div className="flex items-center gap-3 mb-3 pb-2 border-b border-green-200">
                            <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                              {renderMaterialIcon(colors.icon)}
                            </div>
                            <div>
                              <p className="font-bold text-lg text-green-900">{materialType}</p>
                              <p className="text-sm text-gray-600">{requests.length} teslim • Toplam {totalQuantity} adet</p>
                            </div>
                          </div>
                          
                          <div className="space-y-2 pl-2">
                            {requests
                              .sort((a, b) => new Date(b.delivered_date || b.delivered_at) - new Date(a.delivered_date || a.delivered_at))
                              .map(request => (
                              <div key={request.id} className="flex items-center justify-between py-2 border-b border-green-100 last:border-0">
                                <div>
                                  <span className="font-medium text-green-800">{request.quantity} adet</span>
                                  {request.description && (
                                    <span className="text-sm text-gray-500 ml-2">- {request.description}</span>
                                  )}
                                  {request.delivery_note && (
                                    <span className="text-sm text-gray-500 ml-2">- {request.delivery_note}</span>
                                  )}
                                </div>
                                <div className="text-right">
                                  <Badge className="bg-green-600 text-white text-xs">
                                    <CheckCircle className="h-3 w-3 mr-1" />
                                    Teslim Edildi
                                  </Badge>
                                  <p className="text-xs text-gray-500 mt-1">
                                    <span className="text-orange-600">Talep:</span> {new Date(request.requested_date || request.created_at).toLocaleDateString('tr-TR')}
                                  </p>
                                  <p className="text-xs text-green-700">
                                    <span className="text-green-600">Teslim:</span> {new Date(request.delivered_date || request.delivered_at).toLocaleDateString('tr-TR')}
                                  </p>
                                  {request.delivered_by_name && (
                                    <p className="text-xs text-green-700 font-medium">
                                      Teslim Eden: {request.delivered_by_role === 'formen' ? 'Formen ' : ''}
                                      {request.delivered_by_role === 'mudur' ? 'Müdür ' : ''}
                                      {request.delivered_by_role === 'mudur_yardimcisi' ? 'Müd. Yrd. ' : ''}
                                      {request.delivered_by_role === 'bolge_muhendisi' ? 'Böl. Müh. ' : ''}
                                      {request.delivered_by_name}
                                    </p>
                                  )}
                                  {request.delivery_note && (
                                    <p className="text-xs text-blue-700 mt-1 pt-1 border-t border-green-200">
                                      <span className="font-medium">💬 Teslim Notu:</span> {request.delivery_note}
                                    </p>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* REDDEDİLEN MALZEMELER TAB */}
          <TabsContent value="rejected" className="mt-4">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-red-700">
                  <XCircle className="h-5 w-5" />
                  Reddedilen Taleplerim
                </CardTitle>
              </CardHeader>
              <CardContent>
                {groupedRequests.rejected.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Reddedilen talebiniz bulunmuyor</p>
                ) : (
                  <div className="space-y-4">
                    {rejectedTypes.map(materialType => {
                      const requests = rejectedByType[materialType];
                      const colors = materialColors[materialType] || materialColors['Diğer'];
                      const totalQuantity = requests.reduce((sum, r) => sum + r.quantity, 0);
                      
                      return (
                        <div key={materialType} className="p-4 rounded-lg border-2 border-red-300 bg-gradient-to-r from-red-50 to-pink-50">
                          <div className="flex items-center gap-3 mb-3 pb-2 border-b border-red-200">
                            <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                              {renderMaterialIcon(colors.icon)}
                            </div>
                            <div>
                              <p className="font-bold text-lg text-red-900">{materialType}</p>
                              <p className="text-sm text-gray-600">{requests.length} red • Toplam {totalQuantity} adet</p>
                            </div>
                          </div>
                          
                          <div className="space-y-2 pl-2">
                            {requests
                              .sort((a, b) => new Date(b.rejected_date || b.updated_at) - new Date(a.rejected_date || a.updated_at))
                              .map(request => (
                              <div key={request.id} className="py-2 border-b border-red-100 last:border-0">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <span className="font-medium text-red-800">{request.quantity} adet</span>
                                    {request.description && (
                                      <span className="text-sm text-gray-500 ml-2">- {request.description}</span>
                                    )}
                                  </div>
                                  <div className="text-right">
                                    <Badge className="bg-red-600 text-white text-xs">
                                      <XCircle className="h-3 w-3 mr-1" />
                                      Reddedildi
                                    </Badge>
                                    <p className="text-xs text-gray-500 mt-1">
                                      <span className="text-orange-600">Talep:</span> {new Date(request.requested_date || request.created_at).toLocaleDateString('tr-TR')}
                                    </p>
                                    <p className="text-xs text-red-700">
                                      <span className="text-red-600">Red:</span> {new Date(request.rejected_date || request.updated_at).toLocaleDateString('tr-TR')}
                                    </p>
                                    {request.rejected_by_name && (
                                      <p className="text-xs text-gray-500">Reddeden: {request.rejected_by_name}</p>
                                    )}
                                  </div>
                                </div>
                                {request.rejection_reason && (
                                  <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-800">
                                    <strong>Red Nedeni:</strong> {request.rejection_reason}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  // Diğer roller için Tab yapısı ile görünüm (Müdür, Müdür Yardımcısı, Bölge Mühendisi, Formen)
  
  // Kullanıcının erişebileceği bölgeleri belirle
  const currentUser = personnel.find(p => p.id === personnelId);
  const getAccessibleRegions = () => {
    if (isAdmin || isMudur) return regions;
    if (isMudurYardimcisi) {
      // Müdür yardımcısı: Kendi region_ids veya altındaki bölge mühendislerinin bölgeleri
      const myRegionIds = currentUser?.region_ids || [];
      if (myRegionIds.length > 0) {
        return regions.filter(r => myRegionIds.includes(r.id));
      }
      // Eğer region_ids yoksa, tüm bölgeleri göster (geçici çözüm)
      return regions;
    }
    if (isBolgeMuhendisi) {
      // Bölge mühendisi: Region'da assigned_bolge_muhendisi_id == personnelId olan bölgeler
      return regions.filter(r => r.assigned_bolge_muhendisi_id === personnelId);
    }
    if (isFormen) {
      // Formen: Region'da assigned_formen_ids içinde personnelId olan bölgeler
      return regions.filter(r => r.assigned_formen_ids?.includes(personnelId));
    }
    return regions;
  };
  
  const accessibleRegions = getAccessibleRegions();
  
  // Kullanıcının erişebileceği personeli belirle
  const getAccessiblePersonnel = () => {
    if (isAdmin || isMudur) return personnel;
    
    if (isMudurYardimcisi) {
      // Müdür yardımcısının erişebileceği bölgeler üzerinden personeli bul
      const myRegions = accessibleRegions;
      const formenIds = new Set();
      myRegions.forEach(r => {
        r.assigned_formen_ids?.forEach(fid => formenIds.add(fid));
      });
      
      return personnel.filter(p => {
        // Kendisi
        if (p.id === personnelId) return true;
        // Bu bölgelerdeki formenler
        if (formenIds.has(p.id)) return true;
        // Bu formenlerin altındaki park görevlileri
        if (formenIds.has(p.formen_id)) return true;
        return false;
      });
    }
    
    if (isBolgeMuhendisi) {
      // Bölge mühendisinin bölgelerindeki formenler ve park görevlileri
      const myRegions = accessibleRegions;
      const formenIds = new Set();
      myRegions.forEach(r => {
        r.assigned_formen_ids?.forEach(fid => formenIds.add(fid));
      });
      
      return personnel.filter(p => {
        if (p.id === personnelId) return true;
        if (p.bolge_muhendisi_id === personnelId) return true;
        if (formenIds.has(p.id)) return true;
        if (formenIds.has(p.formen_id)) return true;
        return false;
      });
    }
    
    if (isFormen) {
      return personnel.filter(p => 
        p.formen_id === personnelId || 
        p.parent_id === personnelId ||
        p.id === personnelId
      );
    }
    return personnel;
  };
  
  const accessiblePersonnel = getAccessiblePersonnel();

  // ---------------------------------------------------------------------------
  // Hiyerarşik personel filtresi (MY > BM > Formen > Park Görevlisi)
  // Her personel kaydı yalnızca DOĞRUDAN üstünü tutar:
  //   park_gorevlisi.formen_id, formen.bolge_muhendisi_id, bolge_muhendisi.mudur_yardimcisi_id
  // Bir kişinin MY/BM/Formen atalarını bulmak için zincir yukarı yürünür.
  // ---------------------------------------------------------------------------
  const personById = new Map(personnel.map(p => [p.id, p]));

  // Bir kişinin doğrudan üstünün id'si
  const directParentId = (p) => {
    if (!p) return null;
    if (p.role === 'park_gorevlisi') return p.formen_id || p.parent_id || null;
    if (p.role === 'formen') return p.bolge_muhendisi_id || p.parent_id || null;
    if (p.role === 'bolge_muhendisi') return p.mudur_yardimcisi_id || p.parent_id || null;
    return p.parent_id || null; // mudur_yardimcisi / mudur
  };

  // Bir kişinin MY/BM/Formen atalarını döndürür (kendisi o rolse kendi id'si)
  const chainCache = new Map();
  const chainOf = (p) => {
    if (!p) return { my: null, bm: null, formen: null };
    if (chainCache.has(p.id)) return chainCache.get(p.id);
    const anc = [];
    let cur = directParentId(p);
    let guard = 0;
    while (cur && guard++ < 12) {
      const node = personById.get(cur);
      if (!node || anc.includes(node)) break;
      anc.push(node);
      cur = directParentId(node);
    }
    const findRole = (role) =>
      p.role === role ? p.id : (anc.find(a => a.role === role)?.id || null);
    const result = {
      my: findRole('mudur_yardimcisi'),
      bm: findRole('bolge_muhendisi'),
      formen: findRole('formen'),
    };
    chainCache.set(p.id, result);
    return result;
  };

  // Hangi hiyerarşi select'leri gösterilecek (izleyicinin rolüne göre)
  const showMYFilter = isAdmin || isMudur;
  const showBMFilter = isAdmin || isMudur || isMudurYardimcisi;
  const showFormenFilter = isAdmin || isMudur || isMudurYardimcisi || isBolgeMuhendisi;
  const showParkFilter = true; // Tüm yönetici panellerinde park görevlisi seçilebilir

  // Cascading seçenekler — her seviye üst seçimlere göre daralır
  const myOptions = accessiblePersonnel.filter(p => p.role === 'mudur_yardimcisi');
  const bmOptions = accessiblePersonnel.filter(p =>
    p.role === 'bolge_muhendisi' &&
    (filterMY === 'all' || chainOf(p).my === filterMY)
  );
  const formenOptions = accessiblePersonnel.filter(p => {
    if (p.role !== 'formen') return false;
    const c = chainOf(p);
    if (filterMY !== 'all' && c.my !== filterMY) return false;
    if (filterBM !== 'all' && c.bm !== filterBM) return false;
    return true;
  });
  const parkOptions = accessiblePersonnel.filter(p => {
    if (p.role !== 'park_gorevlisi') return false;
    const c = chainOf(p);
    if (filterMY !== 'all' && c.my !== filterMY) return false;
    if (filterBM !== 'all' && c.bm !== filterBM) return false;
    if (filterFormen !== 'all' && c.formen !== filterFormen) return false;
    return true;
  });

  const hasHierarchyFilter =
    filterMY !== 'all' || filterBM !== 'all' || filterFormen !== 'all' || filterPark !== 'all';

  // Üst seviye değişince alt seviyeleri VE bölge seçimini sıfırla
  // (bölge listesi seçilen kişinin bölgeleriyle daralacağından, eski seçim geçersiz olabilir)
  const handleSelectMY = (v) => { setFilterMY(v); setFilterBM('all'); setFilterFormen('all'); setFilterPark('all'); setFilterRegion('all'); };
  const handleSelectBM = (v) => { setFilterBM(v); setFilterFormen('all'); setFilterPark('all'); setFilterRegion('all'); };
  const handleSelectFormen = (v) => { setFilterFormen(v); setFilterPark('all'); setFilterRegion('all'); };
  const handleSelectPark = (v) => { setFilterPark(v); setFilterRegion('all'); };

  const clearHierarchyFilters = () => {
    setFilterMY('all'); setFilterBM('all'); setFilterFormen('all'); setFilterPark('all');
  };

  // Bir alıcının (mr.personnel_id) seçili hiyerarşi filtrelerine uyup uymadığı
  const recipientMatchesHierarchy = (personId) => {
    const p = personById.get(personId);
    if (!p) return false;
    const c = chainOf(p);
    if (filterMY !== 'all' && c.my !== filterMY) return false;
    if (filterBM !== 'all' && c.bm !== filterBM) return false;
    if (filterFormen !== 'all' && c.formen !== filterFormen) return false;
    if (filterPark !== 'all' && p.id !== filterPark) return false;
    return true;
  };

  // Bir kişinin ait olduğu bölge id'leri. Bölge filtresi ve bölge açılır
  // listesinin daraltılması bunu kullanır (ikisi tutarlı olsun diye tek kaynak).
  //   - Formen / BM: bölge tablosundaki doğrudan atamalar (assigned_formen_ids,
  //     assigned_bolge_muhendisi_id)
  //   - Park görevlileri: atandıkları parkların region_id'si (asıl sinyal)
  //   - Hiçbiri yoksa: kişinin kendi region_ids / region_id alanları (yedek)
  const regionIdsCache = new Map();
  const personRegionIds = (person) => {
    if (!person) return new Set();
    if (regionIdsCache.has(person.id)) return regionIdsCache.get(person.id);
    const ids = new Set();

    regions.forEach(r => {
      if (r.assigned_formen_ids?.includes(person.id)) ids.add(r.id);
      if (r.assigned_bolge_muhendisi_id === person.id) ids.add(r.id);
    });

    const assignedParks = parks.filter(p => p.assigned_personnel_ids?.includes(person.id));
    if (ids.size === 0 && assignedParks.length > 0) {
      assignedParks.forEach(p => { if (p.region_id) ids.add(p.region_id); });
    } else if (ids.size === 0) {
      (person.region_ids || []).forEach(rid => ids.add(rid));
      if (person.region_id) ids.add(person.region_id);
    }

    regionIdsCache.set(person.id, ids);
    return ids;
  };

  // Bölge açılır listesi: bir hiyerarşi seçimi varsa, yalnızca o kapsamdaki
  // personelin baktığı bölgeleri göster (örn. Formen Vedat → sadece Vedat'ın bölgeleri).
  const regionOptions = (() => {
    if (!hasHierarchyFilter) return accessibleRegions;
    const ids = new Set();
    personnel.forEach(p => {
      if (recipientMatchesHierarchy(p.id)) {
        personRegionIds(p).forEach(rid => ids.add(rid));
      }
    });
    return accessibleRegions.filter(r => ids.has(r.id));
  })();

  // Bölge + personel filtrelerini uygula (malzeme tipi HARİÇ).
  // İstatistik kartları bu kapsamı kullanır; böylece bir bölge seçilince kartlar
  // o bölgedeki tüm malzeme tiplerini gösterir (kart seçimi tipleri sıfırlamaz).
  const applyScopeFilters = (requests) => {
    let filtered = requests;

    // Bölge filtresi - Bir malzeme dağıtımının bölgesi, ALICI personelin
    // (mr.personnel_id) bölgesidir. Talepte park/bölge alanı tutulmadığı için
    // (form park sormuyor) alıcının bölgesini belirlemek gerekir.
    //
    // ÖNEMLİ: Park görevlilerinin bölgesi `region_ids` ile değil, atandıkları
    // PARKLAR üzerinden belirlenir (App.js standardı). Bu yüzden öncelik park
    // atamalarındadır; formen/bölge mühendisi için bölge tablosundaki atamalar
    // kullanılır. Formen ilişkisine (formen_id) göre eşleştirme YAPILMAZ — bir
    // formen tüm bölgelerinde "ait" sayıldığından her bölgeye tüm ekibi çekerdi.
    if (filterRegion !== "all") {
      // Seçili bölgeye ait alıcıların id'leri (personRegionIds tek kaynak)
      const regionPersonnelIds = new Set(
        personnel.filter(p => personRegionIds(p).has(filterRegion)).map(p => p.id)
      );

      // Yalnızca ALICIYA göre filtrele (formen_id'ye göre değil)
      filtered = filtered.filter(mr => regionPersonnelIds.has(mr.personnel_id));
    }

    // Hiyerarşik personel filtresi (alıcıya göre): MY > BM > Formen > Park Görevlisi
    if (hasHierarchyFilter) {
      filtered = filtered.filter(mr => recipientMatchesHierarchy(mr.personnel_id));
    }

    return filtered;
  };

  // Bölge + personel kapsamlı set (malzeme tipi filtresi uygulanmamış) — kartların
  // istatistikleri ve "Tümü" sayıları bunu kullanır.
  const scopedRequests = applyScopeFilters(filteredRequests);

  // Görüntülenen talepler: bölge + personel + malzeme tipi
  const displayRequests = filterMaterialType !== "all"
    ? scopedRequests.filter(mr => mr.material_type === filterMaterialType)
    : scopedRequests;

  // İstatistikler — seçili bölge/personel kapsamında, içinde bulunulan yıl için.
  // Malzeme tipi filtresinden bağımsızdır (tüm tip kartları görünür kalır).
  const currentYear = new Date().getFullYear();
  const yearRequests = scopedRequests.filter(mr =>
    new Date(mr.created_at).getFullYear() === currentYear
  );

  const materialStats = {};
  yearRequests.forEach(mr => {
    if (!materialStats[mr.material_type]) {
      materialStats[mr.material_type] = {
        totalQuantity: 0,
        requestCount: 0,
        delivered: 0,
        pending: 0,
        rejected: 0
      };
    }
    materialStats[mr.material_type].totalQuantity += mr.quantity;
    materialStats[mr.material_type].requestCount++;
    if (mr.status === 'delivered') {
      materialStats[mr.material_type].delivered++;
    } else if (mr.status === 'rejected') {
      materialStats[mr.material_type].rejected++;
    } else {
      materialStats[mr.material_type].pending++;
    }
  });

  // Duruma göre grupla
  const displayGrouped = {
    pending: displayRequests.filter(mr => mr.status === 'pending'),
    delivered: displayRequests.filter(mr => mr.status === 'delivered'),
    rejected: displayRequests.filter(mr => mr.status === 'rejected')
  };
  
  // Malzeme tipine göre grupla (her durum için)
  const groupByMaterialType = (requests) => {
    const grouped = {};
    requests.forEach(request => {
      const type = request.material_type || 'Diğer';
      if (!grouped[type]) grouped[type] = [];
      grouped[type].push(request);
    });
    return grouped;
  };
  
  const pendingByType = groupByMaterialType(displayGrouped.pending);
  const deliveredByType = groupByMaterialType(displayGrouped.delivered);
  const rejectedByType = groupByMaterialType(displayGrouped.rejected);

  return (
    <div className="space-y-4">
      {/* Başlık ve Yeni Talep Butonu */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-800">Malzeme Yönetimi</h2>
        {has('malzeme.olustur') && (
          <Button
            onClick={onCreateRequest}
            className="flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Yeni Malzeme Talebi Oluştur
          </Button>
        )}
      </div>

      {/* Filtreleme Alanı */}
      <Card className="bg-gray-50">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Malzeme Tipi Filtresi */}
            <div className="flex-1 min-w-[150px]">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Malzeme Tipi</label>
              <SearchableSelect
                className="w-full text-sm"
                value={filterMaterialType}
                onChange={(v) => setFilterMaterialType(v)}
                allLabel="Tüm Malzemeler"
                allValue="all"
                searchPlaceholder="Malzeme ara..."
                options={orderedMaterialTypes.map(type => ({ id: type, name: type }))}
              />
            </div>
            
            {/* Bölge Filtresi */}
            <div className="flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Bölge</label>
              <SearchableSelect
                className="w-full text-sm"
                value={filterRegion}
                onChange={(v) => setFilterRegion(v)}
                allLabel={`Tüm Bölgeler (${regionOptions.length})`}
                allValue="all"
                searchPlaceholder="Bölge ara..."
                options={regionOptions
                  .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                  .map(region => ({ id: region.id, name: region.name }))}
              />
            </div>
            
            {/* Hiyerarşik Personel Filtresi: MY > BM > Formen > Park Görevlisi */}
            {showMYFilter && (
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Müdür Yardımcısı</label>
                <SearchableSelect
                  className="w-full text-sm"
                  value={filterMY}
                  onChange={handleSelectMY}
                  allLabel={`Tüm Müd. Yrd. (${myOptions.length})`}
                  allValue="all"
                  searchPlaceholder="Müd. Yrd. ara..."
                  options={myOptions
                    .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                    .map(p => ({ id: p.id, name: p.name }))}
                />
              </div>
            )}

            {showBMFilter && (
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Bölge Mühendisi</label>
                <SearchableSelect
                  className="w-full text-sm"
                  value={filterBM}
                  onChange={handleSelectBM}
                  allLabel={`Tüm Böl. Müh. (${bmOptions.length})`}
                  allValue="all"
                  searchPlaceholder="Böl. Müh. ara..."
                  options={bmOptions
                    .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                    .map(p => ({ id: p.id, name: p.name }))}
                />
              </div>
            )}

            {showFormenFilter && (
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Formen</label>
                <SearchableSelect
                  className="w-full text-sm"
                  value={filterFormen}
                  onChange={handleSelectFormen}
                  allLabel={`Tüm Formenler (${formenOptions.length})`}
                  allValue="all"
                  searchPlaceholder="Formen ara..."
                  options={formenOptions
                    .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                    .map(p => ({ id: p.id, name: p.name }))}
                />
              </div>
            )}

            {showParkFilter && (
              <div className="flex-1 min-w-[180px]">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Park Görevlisi</label>
                <SearchableSelect
                  className="w-full text-sm"
                  value={filterPark}
                  onChange={handleSelectPark}
                  allLabel={`Tüm Park Gör. (${parkOptions.length})`}
                  allValue="all"
                  searchPlaceholder="Park görevlisi ara..."
                  options={parkOptions
                    .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
                    .map(p => ({ id: p.id, name: p.name }))}
                />
              </div>
            )}

            {/* Filtreleri Temizle */}
            {(filterMaterialType !== "all" || filterRegion !== "all" || hasHierarchyFilter) && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setFilterMaterialType("all");
                  setFilterRegion("all");
                  clearHierarchyFilters();
                }}
              >
                Temizle
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Envanter Kartları - Malzeme Tipine Göre İstatistikler */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
        <Card
          className={`cursor-pointer hover:shadow-md transition-all border-2 border-gray-300 bg-gradient-to-br from-gray-50 to-gray-100 ${
            filterMaterialType === "all" ? "ring-2 ring-offset-2 ring-green-500 shadow-lg" : ""
          }`}
          onClick={() => setFilterMaterialType("all")}
        >
          <CardContent className="p-3 text-center">
            <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-2">
              📋
            </div>
            <p className="text-xs font-semibold text-gray-900 truncate">Tümü</p>
            <p className="text-xl font-bold text-gray-800">{displayRequests.reduce((sum, mr) => sum + mr.quantity, 0)}</p>
            <p className="text-xs text-gray-500">{displayRequests.length} talep</p>
            <div className="flex justify-center gap-1 mt-1 text-xs">
              <span className="text-green-600" title="Teslim Edildi">{displayGrouped.delivered.length}✓</span>
              <span className="text-yellow-600" title="Bekleyen">{displayGrouped.pending.length}⏳</span>
              <span className="text-red-600" title="Reddedilen">{displayGrouped.rejected.length}✗</span>
            </div>
          </CardContent>
        </Card>
        {orderedMaterialTypes.map(materialType => {
          const stats = materialStats[materialType] || { totalQuantity: 0, requestCount: 0, delivered: 0, pending: 0, rejected: 0 };
          const colors = materialColors[materialType] || materialColors['Diğer'];
          const isActive = filterMaterialType === materialType;
          const hasActiveFilter = filterMaterialType !== "all";
          
          return (
            <Card 
              key={materialType} 
              className={`cursor-pointer hover:shadow-md transition-all border-2 ${colors.border} bg-gradient-to-br ${colors.gradient} ${
                hasActiveFilter && !isActive ? "opacity-50 grayscale-[0.2]" : ""
              } ${isActive ? "ring-2 ring-offset-2 ring-green-500 shadow-lg" : ""}`}
              onClick={() => setFilterMaterialType(filterMaterialType === materialType ? "all" : materialType)}
            >
              <CardContent className="p-3 text-center">
                <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center mx-auto mb-2`}>
                  {renderMaterialIcon(colors.icon)}
                </div>
                <p className={`text-xs font-semibold ${colors.textBold} truncate`}>{materialType}</p>
                <p className="text-xl font-bold text-gray-800">{stats.totalQuantity}</p>
                <p className="text-xs text-gray-500">{stats.requestCount} talep</p>
                <div className="flex justify-center gap-1 mt-1 text-xs">
                  <span className="text-green-600" title="Teslim Edildi">{stats.delivered}✓</span>
                  <span className="text-yellow-600" title="Bekleyen">{stats.pending}⏳</span>
                  <span className="text-red-600" title="Reddedilen">{stats.rejected}✗</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Genel Özet */}
      <Card className="bg-gradient-to-r from-purple-50 via-blue-50 to-indigo-50 border-purple-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Package className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm text-purple-600 font-medium">{currentYear} Yılı Toplam</p>
                <p className="text-2xl font-bold text-purple-900">
                  {displayRequests.reduce((sum, mr) => sum + mr.quantity, 0)} Adet
                </p>
                <p className="text-xs text-purple-500">
                  {displayRequests.length} talep • {Object.keys(groupByMaterialType(displayRequests)).length} çeşit malzeme
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="text-center">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-1">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
                <p className="text-xs text-green-600 font-semibold">
                  {displayGrouped.delivered.length} Teslim
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mb-1">
                  <Clock className="h-6 w-6 text-yellow-600" />
                </div>
                <p className="text-xs text-yellow-600 font-semibold">
                  {displayGrouped.pending.length} Bekliyor
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-1">
                  <XCircle className="h-6 w-6 text-red-600" />
                </div>
                <p className="text-xs text-red-600 font-semibold">
                  {displayGrouped.rejected.length} Red
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tab Yapısı */}
      <Tabs defaultValue="pending" className="w-full">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Bekleyen ({displayGrouped.pending.length})
          </TabsTrigger>
          <TabsTrigger value="delivered" className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Teslim Edilen ({displayGrouped.delivered.length})
          </TabsTrigger>
          <TabsTrigger value="rejected" className="flex items-center gap-2">
            <XCircle className="h-4 w-4" />
            Reddedilen ({displayGrouped.rejected.length})
          </TabsTrigger>
        </TabsList>

        {/* BEKLEYEN TALEPLER TAB */}
        <TabsContent value="pending" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-orange-700">
                <Clock className="h-5 w-5" />
                Bekleyen Talepler
              </CardTitle>
            </CardHeader>
            <CardContent>
              {displayGrouped.pending.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Bekleyen talep bulunmuyor</p>
              ) : (
                <div className="space-y-4">
                  {Object.keys(pendingByType).sort((a, b) => a.localeCompare(b, 'tr')).map(materialType => {
                    const requests = pendingByType[materialType];
                    const colors = materialColors[materialType] || materialColors['Diğer'];
                    const totalQuantity = requests.reduce((sum, r) => sum + r.quantity, 0);
                    
                    return (
                      <div key={materialType} className={`p-4 rounded-lg border-2 ${colors.border} bg-gradient-to-r ${colors.gradient}`}>
                        <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-200">
                          <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center`}>
                            {renderMaterialIcon(colors.icon)}
                          </div>
                          <div>
                            <p className={`font-bold text-lg ${colors.textBold}`}>{materialType}</p>
                            <p className="text-sm text-gray-600">{requests.length} talep • Toplam {totalQuantity} adet</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          {requests.map(request => (
                            <div key={request.id} className="flex items-center justify-between py-2 px-3 bg-white rounded border">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold">{request.quantity} adet</span>
                                    <span className="text-sm text-gray-600">- {request.personnel_name}</span>
                                    {request.created_by_name && request.created_by_id !== request.personnel_id && (
                                      <span className="text-xs text-blue-600">({request.created_by_name} tarafından)</span>
                                    )}
                                  </div>
                                  {request.description && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      <span className="font-medium">Açıklama:</span> {request.description}
                                    </p>
                                  )}
                                <p className="text-xs text-gray-500">
                                  Talep: {new Date(request.requested_date || request.created_at).toLocaleDateString('tr-TR')}
                                  {request.formen_name && ` • Formen: ${request.formen_name}`}
                                </p>
                              </div>
                              {!isReadOnly && (
                              <div className="flex gap-2">
                                {has('malzeme.onayla') && !isOwnRequest(request) && (
                                <Button size="sm" onClick={() => onDeliverRequest(request)} className="bg-green-600 hover:bg-green-700">
                                  <Truck className="h-4 w-4 mr-1" />
                                  Teslim
                                </Button>
                                )}
                                {has('malzeme.reddet') && !isOwnRequest(request) && (
                                <Button size="sm" variant="outline" className="border-red-300 text-red-600 hover:bg-red-50" onClick={() => setMaterialToReject(request)}>
                                  <XCircle className="h-4 w-4" />
                                </Button>
                                )}
                                {has('malzeme.sil') && (
                                <Button size="sm" variant="destructive" onClick={() => setMaterialToDelete(request)}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                                )}
                              </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TESLİM EDİLEN MALZEMELER TAB */}
        <TabsContent value="delivered" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="h-5 w-5" />
                Teslim Edilen Malzemeler
              </CardTitle>
            </CardHeader>
            <CardContent>
              {displayGrouped.delivered.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Teslim edilen malzeme bulunmuyor</p>
              ) : (
                <div className="space-y-4">
                  {Object.keys(deliveredByType).sort((a, b) => a.localeCompare(b, 'tr')).map(materialType => {
                    const requests = deliveredByType[materialType];
                    const colors = materialColors[materialType] || materialColors['Diğer'];
                    const totalQuantity = requests.reduce((sum, r) => sum + r.quantity, 0);
                    
                    return (
                      <div key={materialType} className="p-4 rounded-lg border-2 border-green-300 bg-gradient-to-r from-green-50 to-emerald-50">
                        <div className="flex items-center gap-3 mb-3 pb-2 border-b border-green-200">
                          <div className="w-10 h-10 bg-green-200 rounded-full flex items-center justify-center">
                            {renderMaterialIcon(colors.icon)}
                          </div>
                          <div>
                            <p className="font-bold text-lg text-green-900">{materialType}</p>
                            <p className="text-sm text-gray-600">{requests.length} teslim • Toplam {totalQuantity} adet</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          {requests
                            .sort((a, b) => new Date(b.delivered_date || b.delivered_at) - new Date(a.delivered_date || a.delivered_at))
                            .map(request => (
                            <div key={request.id} className="flex items-center justify-between py-2 px-3 bg-white rounded border border-green-200">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-green-800">{request.quantity} adet</span>
                                  <span className="text-sm text-gray-600">- {request.personnel_name}</span>
                                </div>
                                {request.description && (
                                  <p className="text-xs text-gray-500 mt-1">
                                    <span className="font-medium">Açıklama:</span> {request.description}
                                  </p>
                                )}
                                {request.delivery_note && (
                                  <p className="text-xs text-blue-700 mt-1">
                                    <span className="font-medium">Teslim Notu:</span> {request.delivery_note}
                                  </p>
                                )}
                                <p className="text-xs text-gray-500">
                                  <span className="text-orange-600">Talep:</span> {new Date(request.requested_date || request.created_at).toLocaleDateString('tr-TR')}
                                  {' • '}
                                  <span className="text-green-600">Teslim:</span> {new Date(request.delivered_date || request.delivered_at).toLocaleDateString('tr-TR')}
                                  {request.delivered_by_name && (
                                    <span className="text-green-700 font-medium">
                                      {' • '}
                                      {request.delivered_by_role === 'formen' ? 'Formen ' : ''}
                                      {request.delivered_by_role === 'mudur' ? 'Müdür ' : ''}
                                      {request.delivered_by_role === 'mudur_yardimcisi' ? 'Müd. Yrd. ' : ''}
                                      {request.delivered_by_role === 'bolge_muhendisi' ? 'Böl. Müh. ' : ''}
                                      {request.delivered_by_name}
                                    </span>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-green-600 text-white">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Teslim Edildi
                                </Badge>
                                {has('malzeme.sil_teslim') && (
                                  <Button size="sm" variant="destructive" onClick={() => setMaterialToDelete(request)}>
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* REDDEDİLEN MALZEMELER TAB */}
        <TabsContent value="rejected" className="mt-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-red-700">
                <XCircle className="h-5 w-5" />
                Reddedilen Talepler
              </CardTitle>
            </CardHeader>
            <CardContent>
              {displayGrouped.rejected.length === 0 ? (
                <p className="text-gray-500 text-center py-4">Reddedilen talep bulunmuyor</p>
              ) : (
                <div className="space-y-4">
                  {Object.keys(rejectedByType).sort((a, b) => a.localeCompare(b, 'tr')).map(materialType => {
                    const requests = rejectedByType[materialType];
                    const colors = materialColors[materialType] || materialColors['Diğer'];
                    const totalQuantity = requests.reduce((sum, r) => sum + r.quantity, 0);
                    
                    return (
                      <div key={materialType} className="p-4 rounded-lg border-2 border-red-300 bg-gradient-to-r from-red-50 to-pink-50">
                        <div className="flex items-center gap-3 mb-3 pb-2 border-b border-red-200">
                          <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center">
                            {renderMaterialIcon(colors.icon)}
                          </div>
                          <div>
                            <p className="font-bold text-lg text-red-900">{materialType}</p>
                            <p className="text-sm text-gray-600">{requests.length} red • Toplam {totalQuantity} adet</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          {requests
                            .sort((a, b) => new Date(b.rejected_date || b.updated_at) - new Date(a.rejected_date || a.updated_at))
                            .map(request => (
                            <div key={request.id} className="py-2 px-3 bg-white rounded border border-red-200">
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-red-800">{request.quantity} adet</span>
                                    <span className="text-sm text-gray-600">- {request.personnel_name}</span>
                                  </div>
                                  {request.description && (
                                    <p className="text-xs text-gray-500 mt-1">
                                      <span className="font-medium">Açıklama:</span> {request.description}
                                    </p>
                                  )}
                                  <p className="text-xs text-gray-500">
                                    <span className="text-orange-600">Talep:</span> {new Date(request.requested_date || request.created_at).toLocaleDateString('tr-TR')}
                                    {' • '}
                                    <span className="text-red-600">Red:</span> {new Date(request.rejected_date || request.updated_at).toLocaleDateString('tr-TR')}
                                    {request.rejected_by_name && (
                                      <span className="text-red-700 font-medium">{' • '}Reddeden: {request.rejected_by_name}</span>
                                    )}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Badge className="bg-red-600 text-white">
                                    <XCircle className="h-3 w-3 mr-1" />
                                    Reddedildi
                                  </Badge>
                                  {has('malzeme.sil_reddedilen') && (
                                    <Button size="sm" variant="destructive" onClick={() => setMaterialToDelete(request)}>
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  )}
                                </div>
                              </div>
                              {request.rejection_reason && (
                                <div className="mt-2 p-2 bg-red-100 rounded text-sm text-red-800">
                                  <strong>Red Nedeni:</strong> {request.rejection_reason}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Silme Dialog */}
      <AlertDialog open={!!materialToDelete} onOpenChange={() => setMaterialToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Malzeme Talebini Sil</AlertDialogTitle>
            <AlertDialogDescription>
              Bu malzeme talebini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              <div className="mt-4 p-3 bg-gray-50 rounded border">
                <p className="text-sm"><strong>Malzeme:</strong> {materialToDelete?.material_type}</p>
                <p className="text-sm"><strong>Adet:</strong> {materialToDelete?.quantity}</p>
                <p className="text-sm"><strong>Talep Eden:</strong> {materialToDelete?.personnel_name || materialToDelete?.requester_name}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>İptal</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDeleteRequest(materialToDelete?.id)}
              className="bg-red-600 hover:bg-red-700"
            >
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reddetme Dialog */}
      <AlertDialog open={!!materialToReject} onOpenChange={() => { setMaterialToReject(null); setRejectionReason(""); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700">Malzeme Talebini Reddet</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mb-3">Bu malzeme talebini reddetmek üzeresiniz. Lütfen red nedenini belirtiniz.</p>
                <div className="p-3 bg-gray-50 rounded border mb-4">
                  <p className="text-sm"><strong>Malzeme:</strong> {materialToReject?.material_type}</p>
                  <p className="text-sm"><strong>Adet:</strong> {materialToReject?.quantity}</p>
                  <p className="text-sm"><strong>Talep Eden:</strong> {materialToReject?.personnel_name || materialToReject?.requester_name}</p>
                  <p className="text-sm"><strong>Talep Tarihi:</strong> {materialToReject?.requested_date ? new Date(materialToReject.requested_date).toLocaleDateString('tr-TR') : '-'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-900">Red Nedeni *</label>
                  <textarea
                    className="w-full mt-1 p-2 border rounded-md text-sm text-gray-900"
                    rows={3}
                    placeholder="Örn: Depoda kürek kalmadı, yeni sipariş bekleniyor..."
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setMaterialToReject(null); setRejectionReason(""); }}>
              İptal
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRejectRequest}
              className="bg-red-600 hover:bg-red-700"
              disabled={!rejectionReason.trim() || rejecting}
            >
              {rejecting ? "Reddediliyor..." : "Reddet"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
